// src/pages/TarotscopeSign.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/seo";
// Adjust this import to match your project:
import CARDS from "../components/card-data"; // or: import { CARDS } from "../data/card-data";
import { cleanCardSlug } from "../utils/cardRoutes";
import { apiUrlWithLang, currentLanguage } from "../utils/locale";
import { localizedPath, translateCardName } from "../utils/i18n";

const API_BASE = "https://tarotfate-api.tarotfate-com.workers.dev";

/* ===== Shared style tokens (matching your Tarotscope page) ===== */
const WRAP = "px-[1vw]";
const MAXW = "max-w-3xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";

/* Page canvas (turq/plum ribbons) */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.12), rgba(118, 159, 171, 0) 40%),
  linear-gradient(180deg, #014C75 10%, #005C83 50%, #016280ff 30%, #0C4B84 72%, #014C75 90%)
`;

/* Where your zodiac icons live */
const ICON_BASE = "/icons";

/* Signs with date ranges */
const SIGNS = [
  { name: "Aries", slug: "aries", dates: "Mar 21 - Apr 19" },
  { name: "Taurus", slug: "taurus", dates: "Apr 20 - May 20" },
  { name: "Gemini", slug: "gemini", dates: "May 21 - Jun 20" },
  { name: "Cancer", slug: "cancer", dates: "Jun 21 - Jul 22" },
  { name: "Leo", slug: "leo", dates: "Jul 23 - Aug 22" },
  { name: "Virgo", slug: "virgo", dates: "Aug 23 - Sep 22" },
  { name: "Libra", slug: "libra", dates: "Sep 23 - Oct 22" },
  { name: "Scorpio", slug: "scorpio", dates: "Oct 23 - Nov 21" },
  { name: "Sagittarius", slug: "sagittarius", dates: "Nov 22 - Dec 21" },
  { name: "Capricorn", slug: "capricorn", dates: "Dec 22 - Jan 19" },
  { name: "Aquarius", slug: "aquarius", dates: "Jan 20 - Feb 18" },
  { name: "Pisces", slug: "pisces", dates: "Feb 19 - Mar 20" },
];
const SIGN_BY_SLUG = Object.fromEntries(SIGNS.map(s => [s.slug, s]));

const SIGN_ES = {
  aries: { name: "Aries", dates: "21 de marzo - 19 de abril" },
  taurus: { name: "Tauro", dates: "20 de abril - 20 de mayo" },
  gemini: { name: "Geminis", dates: "21 de mayo - 20 de junio" },
  cancer: { name: "Cancer", dates: "21 de junio - 22 de julio" },
  leo: { name: "Leo", dates: "23 de julio - 22 de agosto" },
  virgo: { name: "Virgo", dates: "23 de agosto - 22 de septiembre" },
  libra: { name: "Libra", dates: "23 de septiembre - 22 de octubre" },
  scorpio: { name: "Escorpio", dates: "23 de octubre - 21 de noviembre" },
  sagittarius: { name: "Sagitario", dates: "22 de noviembre - 21 de diciembre" },
  capricorn: { name: "Capricornio", dates: "22 de diciembre - 19 de enero" },
  aquarius: { name: "Acuario", dates: "20 de enero - 18 de febrero" },
  pisces: { name: "Piscis", dates: "19 de febrero - 20 de marzo" },
};

/* Tabs */
const TABS = ["yesterday", "today", "tomorrow"];
const TAB_LABELS_ES = { yesterday: "Ayer", today: "Hoy", tomorrow: "Mañana" };
const TAB = "tf-outline-button";
const TAB_ACTIVE = "border-[#9EE7F2]/80 bg-white/15 text-white";

/* ------- card image + slug resolving from card-data.js ------- */
function pickImgField(obj) {
  return obj?.image || obj?.img || obj?.src || obj?.imageUrl || obj?.image_webp || null;
}
const NUM_WORDS = { zero:"0", one:"1", two:"2", three:"3", four:"4", five:"5", six:"6", seven:"7", eight:"8", nine:"9", ten:"10", page:"page", knight:"knight", queen:"queen", king:"king" };
const norm = s => (s || "").toLowerCase().replace(/\s+/g, " ").replace(/['']/g,"'").trim();
function normalizeForCompare(name) {
  const n = norm(name);
  const m = n.match(/^(zero|one|two|three|four|five|six|seven|eight|nine|ten)\s+of\s+(.+)$/);
  if (m && NUM_WORDS[m[1]]) return `${NUM_WORDS[m[1]]} of ${m[2]}`;
  return n;
}
function getFromCards(cardName) {
  const target = normalizeForCompare(cardName);
  if (Array.isArray(CARDS)) {
    let found = CARDS.find(c => normalizeForCompare(c?.name || c?.title || "") === target);
    if (!found) found = CARDS.find(c => normalizeForCompare(c?.name || c?.title || "").includes(target));
    return found || null;
  }
  if (CARDS && typeof CARDS === "object") {
    for (const k of Object.keys(CARDS)) {
      const c = CARDS[k];
      if (normalizeForCompare(c?.name || c?.title || k) === target) return c;
    }
  }
  return null;
}
function getCardImage(cardName) {
  const found = getFromCards(cardName);
  return pickImgField(found) || null;
}
/** Try to get a canonical slug from data; otherwise fallback to a safe slugified name */
function getCardSlug(cardName) {
  const found = getFromCards(cardName);
  let slug = found ? cleanCardSlug(found) : found?.slug || found?.urlSlug || found?.path || null;
  if (slug) return String(slug).replace(/^\/+|\/+$/g, "");
  // fallback slug from readable name
  return norm(cardName).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function looksEnglishTarotscope(text = "") {
  return /\b(Start small|today|your|you can|the card|this card|notice|take one|of Cups|of Swords|of Wands|of Pentacles)\b/i.test(
    String(text)
  );
}

function localizeCardNamesInText(text = "") {
  let output = String(text || "");
  const cards = Array.isArray(CARDS) ? CARDS : Object.values(CARDS || {});
  for (const card of cards) {
    if (!card?.name) continue;
    const translated = translateCardName(card.name);
    if (translated && translated !== card.name) {
      output = output.replace(new RegExp(card.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), translated);
    }
  }
  return output;
}

function fallbackSpanishMessage(signName, tab, cardName) {
  const day = TAB_LABELS_ES[tab]?.toLowerCase() || "hoy";
  const card = translateCardName(cardName || "The Star");
  return `Para ${signName}, ${day} pide una lectura serena y concreta. ${card} marca un tema de claridad, cuidado y accion pequena. Observa que parte de tu dia necesita menos prisa y mas honestidad. Elige un paso simple que puedas cumplir sin forzarte, y deja que esa decision ordene el resto.`;
}

export default function TarotscopeSign() {
  const { slug } = useParams();
  const isSpanish = currentLanguage() === "es";
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("today");
  const [err, setErr] = useState("");

  const signMeta = SIGN_BY_SLUG[slug] || { name: slug, dates: "" };
  const displaySignName = isSpanish ? SIGN_ES[slug]?.name || signMeta.name : signMeta.name;
  const displaySignDates = isSpanish ? SIGN_ES[slug]?.dates || signMeta.dates : signMeta.dates;
  const iconSrc = `${ICON_BASE}/tarotscope_${slug}.webp`;

  useEffect(() => {
    let cancelled = false;
    setErr(""); setData(null);
    (async () => {
      try {
        const r = await fetch(apiUrlWithLang(`${API_BASE}/api/tarotscope/${slug}`), { headers: { "Accept": "application/json" } });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j?.message || `HTTP ${r.status}`);
        }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const entry = useMemo(() => (data ? data.entries?.[tab] || null : null), [data, tab]);
  const displayDate = useMemo(() => (data ? data.dates?.[tab] || "" : ""), [data, tab]);
  const cardImg = useMemo(() => (entry?.card ? getCardImage(entry.card) : null), [entry]);
  const cardSlug = useMemo(() => (entry?.card ? getCardSlug(entry.card) : null), [entry]);

  // SEO
  const pageTitle = isSpanish ? `${displaySignName} Tarotscopo | TarotFate` : `${displaySignName} Tarotscope | TarotFate`;
  const displayTab = isSpanish ? (TAB_LABELS_ES[tab] || tab).toLowerCase() : tab;
  const pageDesc = isSpanish
    ? `Lee el tarotscopo de ${displaySignName} para ${displayTab}. Un mensaje practico y claro para orientar tu dia.`
    : `Read the ${displaySignName} Tarotscope for ${tab}. A grounded, practical message to guide your day.`;
  const canonical = `https://tarotfate.com${localizedPath(`/tarotscope/${slug}`)}`;
  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://tarotfate.com#org",
        name: "TarotFate",
        url: "https://tarotfate.com",
        logo: {
          "@type": "ImageObject",
          url: "https://tarotfate.com/icons/logo.webp",
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": "https://tarotfate.com#website",
        url: "https://tarotfate.com",
        name: "TarotFate",
        publisher: { "@id": "https://tarotfate.com#org" },
        inLanguage: isSpanish ? "es" : "en",
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: pageTitle,
        description: pageDesc,
        isPartOf: { "@id": "https://tarotfate.com#website" },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: `https://tarotfate.com/icons/tarotscope_${slug}.webp`,
        },
        breadcrumb: { "@id": `${canonical}#breadcrumbs` },
        inLanguage: isSpanish ? "es" : "en",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: isSpanish ? "Inicio" : "Home", item: isSpanish ? "https://tarotfate.com/es" : "https://tarotfate.com" },
          { "@type": "ListItem", position: 2, name: isSpanish ? "Tarotscopo" : "Tarotscope", item: `https://tarotfate.com${localizedPath("/tarotscope")}` },
          { "@type": "ListItem", position: 3, name: displaySignName, item: canonical },
        ],
      },
    ],
  };

  return (
    <div className={`${FONT} min-h-screen relative overflow-hidden text-white`} style={{ background: CANVAS_BG }}>
      <Seo
        title={pageTitle}
        description={pageDesc}
        url={canonical}
        image={`https://tarotfate.com/icons/tarotscope_${slug}.webp`}
        keywords={isSpanish ? `${displaySignName} tarotscopo, horoscopo ${displaySignName}, tarot diario ${displaySignName}` : `${displaySignName} tarotscope, ${displaySignName} horoscope, daily tarotscope, tarot ${displaySignName}`}
        schema={schemaGraph}
      />

      {/* ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* Header */}
      <header className={`${WRAP} ${MAXW} pt-8 pb-2 text-center relative z-[1]`}>
        <div className="flex justify-center">
          <Link
            to={localizedPath("/tarotscope")}
            className="tf-outline-button"
          >
            {isSpanish ? "Tarotscopo" : "Tarotscope"}
          </Link>
        </div>

        <div className="mt-3 flex justify-center">
          <img
            src={iconSrc}
            alt={isSpanish ? `Icono de ${displaySignName}` : `${displaySignName} icon`}
            width={96}
            height={96}
            loading="eager"
            decoding="async"
            draggable="false"
            className="w-24 h-24 object-contain select-none pointer-events-none"
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>

        <h1 className="mt-2 text-3xl font-semibold">
          {displaySignName}
          <span className="sr-only"> {isSpanish ? "Tarotscopo" : "Tarotscope"}</span>
        </h1>

        {displaySignDates && <p className="mt-1 text-white/80">{displaySignDates}</p>}

        <div className="flex items-center justify-center mt-3" aria-hidden>
          <div className="flex-1 max-w-sm h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </header>

      {/* Body */}
      <main className={`${WRAP} ${MAXW} pb-16 relative z-[1]`}>
        {/* Tabs */}
        <div className="mt-4 flex justify-center gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`${TAB} ${tab === t ? TAB_ACTIVE : ""}`}
            >
              {isSpanish ? TAB_LABELS_ES[t] : t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Date */}
        <div className="mt-4 text-center">
          {displayDate ? (
            <p className="text-white text-base">{displayDate}</p>
          ) : (
            <p className="text-white/50 text-sm">{isSpanish ? "Cargando fecha..." : "Loading date..."}</p>
          )}
        </div>

        {/* Content */}
        <section className="mt-6">
          {!data && !err && (
            <div className="text-white/80 text-center">{isSpanish ? "Cargando tarotscopo..." : "Loading Tarotscope..."}</div>
          )}
          {err && (
            <div className="text-red-200/90 text-center">
              {isSpanish ? "No se pudo cargar el tarotscopo en este momento." : `Could not load Tarotscope at this moment... ${err}`}
            </div>
          )}

          {data && !entry && (
            <div className="text-white/80 text-center">
              {tab === "tomorrow"
                ? isSpanish ? "El tarotscopo de manana aparecera cuando este listo." : "Tomorrow's tarotscope will appear once it's generated."
                : isSpanish ? "No hay entrada para este dia." : "No entry found for this day."}
            </div>
          )}

          {entry && (
            <div
              className="
                mt-4 rounded-2xl overflow-hidden
                bg-gradient-to-b from-white/5 via-[#14656a]/25 to-[#0f3d3f]/10
                border border-white/10
              "
            >
              {/* Reading content */}
              <div className="p-4 sm:p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:gap-5">
                  {/* Message */}
                  <article className="order-1 md:order-2 md:pr-2">
                    <p className="text-base sm:text-base leading-relaxed text-white break-words">
                      {isSpanish
                        ? looksEnglishTarotscope(entry.message)
                          ? fallbackSpanishMessage(displaySignName, tab, entry.card)
                          : localizeCardNamesInText(entry.message)
                        : entry.message}
                    </p>
                  </article>

                  {/* Card image */}
                  <div className="order-2 md:order-1 mt-4 md:mt-0 md:mb-0 md:shrink-0 md:self-start">
                    {cardImg ? (
                      cardSlug ? (
                        <Link
                          to={localizedPath(`/tarot-card/${cardSlug}`)}
                          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-lg"
                          aria-label={entry.card ? `${translateCardName(entry.card)} ${isSpanish ? "significado" : "meaning"}` : isSpanish ? "Significado de carta de tarot" : "Tarot card meaning"}
                        >
                          <img
                            src={cardImg}
                            alt={entry.card ? `${translateCardName(entry.card)} tarot` : isSpanish ? "Carta de tarot" : "Tarot card"}
                            className="w-36 sm:w-40 md:w-32 h-auto rounded-lg ring-1 ring-white/15 shadow-md object-contain mx-auto md:mx-0 select-none pointer-events-none"
                            loading="lazy"
                            decoding="async"
                            draggable="false"
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        </Link>
                      ) : (
                        <img
                          src={cardImg}
                          alt={entry.card ? `${translateCardName(entry.card)} tarot` : isSpanish ? "Carta de tarot" : "Tarot card"}
                          className="w-36 sm:w-40 md:w-32 h-auto rounded-lg ring-1 ring-white/15 shadow-md object-contain mx-auto md:mx-0 select-none pointer-events-none"
                          loading="lazy"
                          decoding="async"
                          draggable="false"
                          onContextMenu={(e) => e.preventDefault()}
                        />
                      )
                    ) : (
                      cardSlug ? (
                        <Link
                          to={localizedPath(`/tarot-card/${cardSlug}`)}
                          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-lg"
                          aria-label={entry.card ? `${translateCardName(entry.card)} ${isSpanish ? "significado" : "meaning"}` : isSpanish ? "Significado de carta de tarot" : "Tarot card meaning"}
                        >
                          <div
                            className="w-36 sm:w-40 md:w-32 aspect-[5/8] rounded-lg ring-1 ring-white/15 shadow-md bg-white/10 grid place-items-center text-white/60 text-xs mx-auto md:mx-0"
                            aria-label={isSpanish ? "Imagen de carta no disponible" : "Card image not available"}
                          >
                            {isSpanish ? "Carta" : "Card"}
                            <span className="sr-only">{translateCardName(entry.card)}</span>
                          </div>
                        </Link>
                      ) : (
                        <div
                          className="w-36 sm:w-40 md:w-32 aspect-[5/8] rounded-lg ring-1 ring-white/15 shadow-md bg-white/10 grid place-items-center text-white/60 text-xs mx-auto md:mx-0"
                          aria-label={isSpanish ? "Imagen de carta no disponible" : "Card image not available"}
                        >
                          {isSpanish ? "Carta" : "Card"}
                          <span className="sr-only">{translateCardName(entry.card)}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Separator (always shown) */}
          <div className="mt-10 flex items-center justify-center">
            <div className="w-3/4 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          </div>

          {/* Other signs section (always rendered) */}
          <div className="mt-8 text-center text-white/85">
            <p className="mb-4 text-base">
              {isSpanish ? "Explora más " : "Explore more "}
              <Link to={localizedPath("/tarotscope")} className="underline hover:text-white">
                {isSpanish ? "tarotscopos" : "tarotscopes"}
              </Link>
              {isSpanish ? " de otros signos zodiacales:" : " for other zodiac signs:"}
            </p>
            <div className="tf-outline-button-row">
              {SIGNS.filter(s => s.slug !== slug).map(sign => {
                const label = isSpanish ? SIGN_ES[sign.slug]?.name || sign.name : sign.name;
                return (
                  <Link
                    key={sign.slug}
                    to={localizedPath(`/tarotscope/${sign.slug}`)}
                    className="tf-outline-button"
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
